import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface Customer {
  id: number;
  name: string;
  email?: string;
  payment_terms: number;
}

interface LineItem {
  description: string;
  quantity: number;
  unit_price: number;
  vat_rate: string;
  vat_amount: number;
  line_total: number;
}

interface CreateInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  customers: Customer[];
}

const VAT_RATES = [
  { value: 'STANDARD', label: 'Standard (20%)', rate: 0.20 },
  { value: 'REDUCED', label: 'Reduced (5%)', rate: 0.05 },
  { value: 'ZERO', label: 'Zero (0%)', rate: 0.00 },
  { value: 'EXEMPT', label: 'Exempt', rate: 0.00 }
];

const CreateInvoiceDialog: React.FC<CreateInvoiceDialogProps> = ({
  open,
  onClose,
  onSuccess,
  customers
}) => {
  const [formData, setFormData] = useState({
    customer_id: '',
    invoice_date: new Date(),
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
    payment_terms: 30,
    notes: ''
  });

  const [lineItems, setLineItems] = useState<LineItem[]>([
    {
      description: '',
      quantity: 1,
      unit_price: 0,
      vat_rate: 'STANDARD',
      vat_amount: 0,
      line_total: 0
    }
  ]);

  const [totals, setTotals] = useState({
    subtotal: 0,
    total_vat: 0,
    total_amount: 0
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    calculateTotals();
  }, [lineItems]);

  const calculateLineItem = (item: LineItem) => {
    const netAmount = item.quantity * item.unit_price;
    const vatRate = VAT_RATES.find(rate => rate.value === item.vat_rate)?.rate || 0;
    const vatAmount = netAmount * vatRate;
    const lineTotal = netAmount + vatAmount;

    return {
      ...item,
      vat_amount: vatAmount,
      line_total: lineTotal
    };
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalVat = 0;

    lineItems.forEach(item => {
      const calculated = calculateLineItem(item);
      subtotal += item.quantity * item.unit_price;
      totalVat += calculated.vat_amount;
    });

    setTotals({
      subtotal,
      total_vat: totalVat,
      total_amount: subtotal + totalVat
    });
  };

  const handleCustomerChange = (customerId: string) => {
    const customer = customers.find(c => c.id === parseInt(customerId));
    if (customer) {
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + customer.payment_terms);
      
      setFormData({
        ...formData,
        customer_id: customerId,
        payment_terms: customer.payment_terms,
        due_date: dueDate
      });
    }
  };

  const handleLineItemChange = (index: number, field: keyof LineItem, value: any) => {
    const updatedItems = [...lineItems];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    
    // Recalculate this line item
    updatedItems[index] = calculateLineItem(updatedItems[index]);
    
    setLineItems(updatedItems);
  };

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      {
        description: '',
        quantity: 1,
        unit_price: 0,
        vat_rate: 'STANDARD',
        vat_amount: 0,
        line_total: 0
      }
    ]);
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length > 1) {
      const updatedItems = lineItems.filter((_, i) => i !== index);
      setLineItems(updatedItems);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Validate required fields
      if (!formData.customer_id) {
        setError('Please select a customer');
        return;
      }

      if (lineItems.some(item => !item.description || item.quantity <= 0 || item.unit_price < 0)) {
        setError('Please fill in all line item details');
        return;
      }

      const invoiceData = {
        customer_id: parseInt(formData.customer_id),
        invoice_date: formData.invoice_date.toISOString().split('T')[0],
        due_date: formData.due_date.toISOString().split('T')[0],
        payment_terms: formData.payment_terms,
        notes: formData.notes,
        line_items: lineItems.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          vat_rate: item.vat_rate
        }))
      };

      const response = await fetch('/api/v1/invoices/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(invoiceData),
      });

      if (response.ok) {
        onSuccess();
        handleClose();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create invoice');
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      setError('Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      customer_id: '',
      invoice_date: new Date(),
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      payment_terms: 30,
      notes: ''
    });
    setLineItems([{
      description: '',
      quantity: 1,
      unit_price: 0,
      vat_rate: 'STANDARD',
      vat_amount: 0,
      line_total: 0
    }]);
    setError('');
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CalculateIcon />
            Create New Invoice
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Invoice Header */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Customer</InputLabel>
                <Select
                  value={formData.customer_id}
                  label="Customer"
                  onChange={(e) => handleCustomerChange(e.target.value)}
                >
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id.toString()}>
                      {customer.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Invoice Date"
                value={formData.invoice_date}
                onChange={(date) => setFormData({ ...formData, invoice_date: date || new Date() })}
                slotProps={{
                  textField: { fullWidth: true }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <DatePicker
                label="Due Date"
                value={formData.due_date}
                onChange={(date) => setFormData({ ...formData, due_date: date || new Date() })}
                slotProps={{
                  textField: { fullWidth: true }
                }}
              />
            </Grid>
          </Grid>

          {/* Line Items */}
          <Typography variant="h6" gutterBottom>
            Line Items
          </Typography>
          
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell width="100px">Qty</TableCell>
                  <TableCell width="120px">Unit Price</TableCell>
                  <TableCell width="120px">VAT Rate</TableCell>
                  <TableCell width="100px">VAT</TableCell>
                  <TableCell width="120px">Total</TableCell>
                  <TableCell width="50px"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Item description"
                        value={item.description}
                        onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => handleLineItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{ startAdornment: '£' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        fullWidth
                        size="small"
                        value={item.vat_rate}
                        onChange={(e) => handleLineItemChange(index, 'vat_rate', e.target.value)}
                      >
                        {VAT_RATES.map((rate) => (
                          <MenuItem key={rate.value} value={rate.value}>
                            {rate.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        £{item.vat_amount.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        £{item.line_total.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => removeLineItem(index)}
                        disabled={lineItems.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            startIcon={<AddIcon />}
            onClick={addLineItem}
            sx={{ mb: 3 }}
          >
            Add Line Item
          </Button>

          {/* Totals */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Box sx={{ minWidth: 300 }}>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>Subtotal:</Typography>
                <Typography>£{totals.subtotal.toFixed(2)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>VAT:</Typography>
                <Typography>£{totals.total_vat.toFixed(2)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6">Total:</Typography>
                <Typography variant="h6">£{totals.total_amount.toFixed(2)}</Typography>
              </Box>
            </Box>
          </Box>

          {/* Notes */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (Optional)"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Additional notes or terms..."
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Invoice'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CreateInvoiceDialog;
